import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { developerService, type DeveloperCreateUserRequest } from '../services';

interface DeveloperUserFormData {
  name: string;
  email: string;
  password: string;
  role: 'DEVELOPER' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';
  cnpj: string;
}

interface DeveloperUser extends DeveloperCreateUserRequest {
  id: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const DeveloperUserManagement: React.FC = () => {
  const [users, setUsers] = useState<DeveloperUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<DeveloperUser | null>(null);
  const [formData, setFormData] = useState<DeveloperUserFormData>({
    name: '',
    email: '',
    password: '',
    role: 'OPERATOR',
    cnpj: '',
  });
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [cnpjFilter, setCnpjFilter] = useState('');

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await developerService.listUsersFromAnyCompany(cnpjFilter.trim() || undefined);
      setUsers(usersData as DeveloperUser[]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyCnpjFilter = async () => {
    await loadUsers();
  };

  const handleOpenCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'OPERATOR',
      cnpj: '',
    });
    setDialogError('');
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (user: DeveloperUser) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      cnpj: user.cnpj,
    });
    setDialogError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setDialogError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDialogError('');
    setDialogLoading(true);

    try {
      if (editingUser) {
        await developerService.updateUserFromAnyCompany(editingUser.id, {
          name: formData.name,
          role: formData.role,
          cnpj: formData.cnpj,
        });
      } else {
        if (!formData.password) {
          setDialogError('Senha é obrigatória para criar um novo usuário');
          setDialogLoading(false);
          return;
        }
        await developerService.createUserForAnyCompany(formData);
      }

      handleCloseDialog();
      loadUsers();
    } catch (err: any) {
      setDialogError(err.response?.data?.error || 'Erro ao salvar usuário');
    } finally {
      setDialogLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'DEVELOPER':
        return 'secondary';
      case 'ADMIN':
        return 'error';
      case 'MANAGER':
        return 'warning';
      case 'OPERATOR':
        return 'info';
      case 'VIEWER':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestão Master de Usuários
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Como usuário DEVELOPER, você pode gerenciar usuários de qualquer empresa, filtrando por CNPJ.
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { xs: 'stretch', sm: 'flex-end' },
        }}
      >
        <TextField
          label="CNPJ (opcional)"
          variant="outlined"
          value={cnpjFilter}
          onChange={(e) => setCnpjFilter(e.target.value)}
          size="small"
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleApplyCnpjFilter}
          disabled={loading}
          sx={{ minWidth: { sm: 140 } }}
        >
          Aplicar CNPJ
        </Button>
        <Box flexGrow={1} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Novo Usuário (qualquer empresa)
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Função</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={user.role} color={getRoleColor(user.role) as any} size="small" />
                  </TableCell>
                  <TableCell>{user.cnpj}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Ativo' : 'Inativo'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEditDialog(user)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Editar Usuário (qualquer empresa)' : 'Novo Usuário (qualquer empresa)'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              label="Nome"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={dialogLoading}
            />

            <TextField
              margin="dense"
              label="E-mail"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={dialogLoading}
            />

            {!editingUser && (
              <TextField
                margin="dense"
                label="Senha"
                type="password"
                fullWidth
                variant="outlined"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                disabled={dialogLoading}
                helperText="Mínimo 6 caracteres"
              />
            )}

            <TextField
              margin="dense"
              label="CNPJ"
              fullWidth
              variant="outlined"
              value={formData.cnpj}
              onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
              required
              disabled={dialogLoading}
            />

            <FormControl fullWidth margin="dense" disabled={dialogLoading}>
              <InputLabel>Função</InputLabel>
              <Select
                value={formData.role}
                label="Função"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
              >
                <MenuItem value="DEVELOPER">Developer</MenuItem>
                <MenuItem value="ADMIN">Administrador</MenuItem>
                <MenuItem value="MANAGER">Gerente</MenuItem>
                <MenuItem value="OPERATOR">Operador</MenuItem>
                <MenuItem value="VIEWER">Visualizador</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={dialogLoading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={dialogLoading}
              startIcon={dialogLoading ? <CircularProgress size={20} /> : null}
            >
              {dialogLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default DeveloperUserManagement;
